import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trophy, Medal, Home, Calendar, Award, ChevronRight } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';
import LineupDetailModal from './LineupDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RankTrophy = ({ rank }) => {
  if (rank === 1) return <Trophy className="w-5 h-5 text-amber-300" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="text-slate-500 font-bold text-sm w-5 text-center inline-block">{rank}</span>;
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const FantasyRankingPage = () => {
  const [tab, setTab] = useState('season');       // season | weekly | monthly
  const [mds, setMds] = useState([]);
  const [mdId, setMdId] = useState('');
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [detailFor, setDetailFor] = useState(null); // { userId, userName, matchDayId }

  const load = async () => {
    setLoading(true); setErr('');
    try {
      let url = `${API}/fantasy/ranking`;
      if (tab === 'weekly' && mdId) url += `?match_day_id=${mdId}`;
      else if (tab === 'monthly') url += `?month=${month}`;
      const { data } = await axios.get(url);
      setData(data);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, mdId, month]);

  // Fetch the list of processed match days for the "weekly" selector
  useEffect(() => {
    if (tab !== 'weekly') return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/fantasy/match-days/processed`);
        setMds(data || []);
        if ((data || []).length > 0) setMdId((prev) => prev || data[0].id);
      } catch { /* noop */ }
    })();
  }, [tab]);

  const rows = data?.rows || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900">
      <header className="border-b border-white/10 bg-slate-950/60 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30 shrink-0">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-black leading-tight tracking-tight">RatingPro Fantasy · Ranking</div>
              <div className="text-[10px] sm:text-xs text-emerald-300/80 italic leading-tight">O Manager Oficial da Nossa Pelada</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/fantasy" className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5">
              <Home className="w-4 h-4" /> Fantasy
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-6">Classificação</h1>

        <div className="flex gap-2 mb-4 overflow-x-auto">
          {[
            { key: 'season', label: 'Temporada' },
            { key: 'monthly', label: 'Mês' },
            { key: 'weekly', label: 'Jornada' },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap ${tab === t.key ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60'}`}
              data-testid={`ranking-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'monthly' && (
          <div className="mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="ranking-month-input" />
          </div>
        )}
        {tab === 'weekly' && mds.length > 0 && (
          <div className="mb-4">
            <select value={mdId} onChange={(e) => setMdId(e.target.value)}
              className="bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="ranking-md-select">
              {mds.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        )}

        {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">{err}</div>}

        <div className="bg-slate-900/60 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
          {loading ? <div className="p-6 text-slate-400">A carregar...</div>
            : rows.length === 0 ? (
              <div className="p-6 text-slate-400 text-center">
                Ainda sem pontuações. Aguarda o admin processar a jornada.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-400 uppercase border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3 w-12">#</th>
                    <th className="px-4 py-3">Treinador</th>
                    {rows[0]?.team_name && <th className="px-4 py-3">Equipa</th>}
                    <th className="px-4 py-3 text-right">Pontos</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const clickable = tab === 'weekly' && mdId;
                    return (
                      <tr key={r.user_id}
                        onClick={clickable ? () => setDetailFor({ userId: r.user_id, userName: r.user_name, matchDayId: mdId }) : undefined}
                        className={`border-b border-white/5 ${i === 0 ? 'bg-amber-500/5' : 'hover:bg-white/5'} ${clickable ? 'cursor-pointer' : ''}`}
                        data-testid={`ranking-row-${i + 1}`}>
                        <td className="px-4 py-3">
                          <RankTrophy rank={i + 1} />
                        </td>
                        <td className="px-4 py-3 text-white font-bold">
                          <div className="flex items-center gap-1.5">
                            {r.user_name}
                            {clickable && <ChevronRight className="w-4 h-4 text-slate-500" />}
                          </div>
                        </td>
                        {r.team_name && <td className="px-4 py-3 text-slate-300">{r.team_name}</td>}
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center gap-1 text-emerald-300 font-black tabular-nums text-lg">
                            <Award className="w-4 h-4" /> {Number(r.total_points).toFixed(1)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
        {tab === 'weekly' && mdId && (
          <div className="mt-2 text-[11px] text-slate-500">Toca num treinador para ver a escalação detalhada da jornada.</div>
        )}
      </main>

      {detailFor && (
        <LineupDetailModal
          matchDayId={detailFor.matchDayId}
          userId={detailFor.userId}
          userName={detailFor.userName}
          onClose={() => setDetailFor(null)}
        />
      )}
    </div>
  );
};

export default FantasyRankingPage;
